import { CustomValidatorErrorEnum, FormControlValidatorEnum } from '@shared/enums';
import { ValidatorFormEntityEnum } from '@shared/enums/validator-form-entity.enum';
import {
    maximumNumberOfSamples,
    maximumSampleSize,
    minimumSampleSize,
    sampleBarcodeCharacterLimit,
} from 'src/app/features/orders/order-setup/_core/constants';

type ValidationMessageKey = {
    key: string;
    params?: Record<string, string | number>;
};

type ValidationMessageConfig = {
    keys: ValidationMessageKey[];
};

type ValidationMessages = Record<string, ValidationMessageConfig>;

function getOrderSetupSampleNameValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'missing' }, { key: 'sampleName' }],
        },
        [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: {
            keys: [{ key: 'uniqueValueFormValidator', params: { fieldName: 'reaction', property: 'sampleName' } }],
        },
    };
}

function getOrderSetupBarcodeValidationMessages(barcodeValue: string): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'missing' }, { key: 'barcode' }],
        },
        [CustomValidatorErrorEnum.INVALID_BARCODE]: {
            keys: [{ key: 'invalid' }, { key: 'barcode' }],
        },
        [FormControlValidatorEnum.MAX_LENGTH]: {
            keys: [
                { key: 'barcode' },
                { key: 'maxLengthFormValidator', params: { limit: sampleBarcodeCharacterLimit } },
            ],
        },
        [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: {
            keys: [{ key: 'uniqueValueFormValidator', params: { fieldName: 'reaction', property: 'barcode' } }],
        },
        [CustomValidatorErrorEnum.EXPIRED_SAMPLE]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.barcode.noLongerAvailable',
                    params: { barcode: barcodeValue ?? '' },
                },
            ],
        },
        [CustomValidatorErrorEnum.DISPOSED_SAMPLE]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.barcode.noLongerAvailable',
                    params: { barcode: barcodeValue ?? '' },
                },
            ],
        },
        [CustomValidatorErrorEnum.USED_BARCODE]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.barcode.usedBarcode',
                },
            ],
        },
        [CustomValidatorErrorEnum.SAMPLE_NOT_RECEIVED]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.barcode.unreceivedSample',
                    params: { barcode: barcodeValue },
                },
            ],
        },
        [CustomValidatorErrorEnum.EMPTY_SAMPLE]: {
            keys: [{ key: 'order.setup.reactionDetails.barcode.sampleUsedTooManyTimes' }],
        },
        [CustomValidatorErrorEnum.BARCODE_ALREADY_USED]: {
            keys: [
                { key: 'order.setup.reactionDetails.barcode.barcodeAlreadyUsed', params: { barcode: barcodeValue } },
            ],
        },
        [CustomValidatorErrorEnum.CONTAMINATED_SAMPLE]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.barcode.contaminated',
                    params: { barcode: barcodeValue ?? '' },
                },
            ],
        },
        [CustomValidatorErrorEnum.TUBE_MISSING_SAMPLE]: {
            keys: [{ key: 'order.setup.reactionDetails.barcode.tubeMissing', params: { barcode: barcodeValue ?? '' } }],
        },
    };
}

function getOrderSetupSizeValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.table.size.validation.message',
                    params: { minSize: minimumSampleSize, maxSize: maximumSampleSize },
                },
            ],
        },
        [FormControlValidatorEnum.MIN]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.table.size.validation.message',
                    params: { minSize: minimumSampleSize, maxSize: maximumSampleSize },
                },
            ],
        },
        [FormControlValidatorEnum.MAX]: {
            keys: [
                {
                    key: 'order.setup.reactionDetails.table.size.validation.message',
                    params: { minSize: minimumSampleSize, maxSize: maximumSampleSize },
                },
            ],
        },
    };
}

function getOrderSetupDnaTypeValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'missing' }, { key: 'dnaType' }],
        },
    };
}

function getOrderSetupPrimerValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'order.setup.reactionDetails.templates.primer.required.message' }],
        },
        [CustomValidatorErrorEnum.INVALID_PRIMER_SELECTED]: {
            keys: [{ key: 'order.setup.reactionDetails.templates.primer.invalidPrimerSelected.message' }],
        },
        [CustomValidatorErrorEnum.MAX_ARRAY_SIZE]: {
            keys: [{ key: 'order.setup.reactionDetails.primer.error.maxArrayLength.message' }],
        },
        [CustomValidatorErrorEnum.OUT_OF_STOCK_PRIMER_SELECTED]: {
            keys: [{ key: 'order.setup.reactionDetails.primer.error.outOfStock.message' }],
        },
    };
}

function getPrimerNameValidationMessages(): ValidationMessages {
    return {
        [CustomValidatorErrorEnum.NOT_UNIQUE]: {
            keys: [{ key: 'orders.setup.primerManagement.primerName.notUnique' }],
        },
        [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: {
            keys: [{ key: 'uniqueValueFormValidator', params: { fieldName: 'primer', property: 'name' } }],
        },
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'orders.setup.primerManagement.primerName.emptyField.error' }],
        },
        [CustomValidatorErrorEnum.EMPTY_INPUT]: {
            keys: [{ key: 'orders.setup.primerManagement.primerName.emptyField.error' }],
        },
    };
}

function getCustomPrimerSequenceValidationMessages(): ValidationMessages {
    return {
        [CustomValidatorErrorEnum.NOT_UNIQUE]: {
            keys: [{ key: 'orders.setup.primerManagement.sequence.notUnique' }],
        },
        [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: {
            keys: [{ key: 'uniqueValueFormValidator', params: { fieldName: 'primer', property: 'sequence' } }],
        },
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'orders.setup.primerManagement.sequence.emptyField.message' }],
        },
    };
}

function getShippedPrimerBarcodeValidationMessages(): ValidationMessages {
    return {
        [CustomValidatorErrorEnum.BACKEND_INVALID]: {
            keys: [{ key: 'orders.setup.numberOfReactions.barcode.backendInvalid.validator' }],
        },
        [CustomValidatorErrorEnum.NOT_UNIQUE]: {
            keys: [{ key: 'orders.setup.numberOfReactions.barcode.unique.validator' }],
        },
        [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: {
            keys: [{ key: 'uniqueValueFormValidator', params: { fieldName: 'primer', property: 'barcode' } }],
        },
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'orders.setup.primerManagement.barcode.emptyField.error' }],
        },
        [CustomValidatorErrorEnum.EMPTY_INPUT]: {
            keys: [{ key: 'orders.setup.primerManagement.barcode.emptyField.error' }],
        },
        [CustomValidatorErrorEnum.BARCODE_ALREADY_USED]: {
            keys: [{ key: 'orders.setup.numberOfReactions.shippedPrimers.barcodeAlreadyUsed' }],
        },
    };
}

function getOrderSetupNumberOfSamplesValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.MAX]: {
            keys: [
                {
                    key: 'orders.setup.numberOfReactions.numberOfSamples.maxValidator',
                    params: { max: maximumNumberOfSamples },
                },
            ],
        },
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [
                {
                    key: 'orders.setup.numberOfReactions.numberOfSamples.required',
                },
            ],
        },
        [FormControlValidatorEnum.MIN]: {
            keys: [{ key: 'orders.setup.numberOfReactions.numberOfSamples.minValidator' }],
        },
    };
}

function getInformedUserFirstNameValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.firstNameValidator' }],
        },
        [CustomValidatorErrorEnum.EMPTY_INPUT]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.firstNameValidator' }],
        },
        [FormControlValidatorEnum.MAX_LENGTH]: {
            keys: [
                { key: 'orders.setup.orderSummary.addUserToInformModal.firstName' },
                { key: 'maxLengthFormValidator', params: { limit: 50 } },
            ],
        },
        [FormControlValidatorEnum.MIN_LENGTH]: {
            keys: [
                { key: 'orders.setup.orderSummary.addUserToInformModal.firstName' },
                { key: 'minLengthFormValidator', params: { limit: 2 } },
            ],
        },
        [CustomValidatorErrorEnum.PATTERN]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.firstNamePattern' }],
        },
    };
}

function getInformedUserLastNameValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.lastNameValidator' }],
        },
        [CustomValidatorErrorEnum.EMPTY_INPUT]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.lastNameValidator' }],
        },
        [FormControlValidatorEnum.MAX_LENGTH]: {
            keys: [
                { key: 'orders.setup.orderSummary.addUserToInformModal.lastName' },
                { key: 'maxLengthFormValidator', params: { limit: 50 } },
            ],
        },
        [FormControlValidatorEnum.MIN_LENGTH]: {
            keys: [
                { key: 'orders.setup.orderSummary.addUserToInformModal.lastName' },
                { key: 'minLengthFormValidator', params: { limit: 2 } },
            ],
        },
        [CustomValidatorErrorEnum.PATTERN]: {
            keys: [{ key: 'orders.setup.orderSummary.addUserToInformModal.lastNamePattern' }],
        },
    };
}

function getInformedUserEmailValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.MAX_LENGTH]: {
            keys: [{ key: 'emailAddress' }, { key: 'maxLengthFormValidator', params: { limit: 200 } }],
        },
        [CustomValidatorErrorEnum.INVALID_EMAIL]: {
            keys: [{ key: 'emailFormValidator' }],
        },
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'emailFormValidator' }],
        },
    };
}

function getPrimerDetailsDrawerPrimerNameValidationMessages(): ValidationMessages {
    return {
        [FormControlValidatorEnum.REQUIRED]: {
            keys: [{ key: 'inventory.primerDetailsDrawer.primerName.requiredError' }],
        },
        [CustomValidatorErrorEnum.EMPTY_INPUT]: {
            keys: [{ key: 'inventory.primerDetailsDrawer.primerName.requiredError' }],
        },
        [FormControlValidatorEnum.MAX_LENGTH]: {
            keys: [{ key: 'inventory.primerDetailsDrawer.primerName.maxLengthError' }],
        },
    };
}

export function globalValidators(dynamicValue: string): Record<ValidatorFormEntityEnum, ValidationMessages> {
    return {
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_1_NUMBER_OF_SAMPLES]:
            getOrderSetupNumberOfSamplesValidationMessages(),
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_2_SAMPLE_NAME]: getOrderSetupSampleNameValidationMessages(),
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_2_BARCODE]: getOrderSetupBarcodeValidationMessages(dynamicValue),
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_2_SIZE]: getOrderSetupSizeValidationMessages(),
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_2_DNA_TYPE]: getOrderSetupDnaTypeValidationMessages(),
        [ValidatorFormEntityEnum.ORDER_SETUP_STEP_2_PRIMER]: getOrderSetupPrimerValidationMessages(),
        [ValidatorFormEntityEnum.CUSTOM_PRIMER_NAME]: getPrimerNameValidationMessages(),
        [ValidatorFormEntityEnum.CUSTOM_PRIMER_SEQUENCE]: getCustomPrimerSequenceValidationMessages(),
        [ValidatorFormEntityEnum.SHIPPED_PRIMER_NAME]: getPrimerNameValidationMessages(),
        [ValidatorFormEntityEnum.SHIPPED_PRIMER_BARCODE]: getShippedPrimerBarcodeValidationMessages(),
        [ValidatorFormEntityEnum.ADD_NEW_INFORMED_USER_FIRST_NAME]: getInformedUserFirstNameValidationMessages(),
        [ValidatorFormEntityEnum.ADD_NEW_INFORMED_USER_LAST_NAME]: getInformedUserLastNameValidationMessages(),
        [ValidatorFormEntityEnum.ADD_NEW_INFORMED_USER_EMAIL]: getInformedUserEmailValidationMessages(),
        [ValidatorFormEntityEnum.RESULTS_PRIMER_DRAWER_PRIMER_NAME]:
            getPrimerDetailsDrawerPrimerNameValidationMessages(),
    };
}
